import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert, Box, Card, Chip, Dialog, DialogContent, DialogTitle, Grid, IconButton,
  MenuItem, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Controller, useForm } from "react-hook-form";
import PageHeaderLayout from "../../components/Layout/PageHeaderLayout";
import { QuickBookService } from "./Service";
import { CustomerService } from "../Customer/Service";

const ok = (response) => response?.success === 1 || response?.success === "1";

const EVENT_TYPE_LABEL = {
  QUICK_BOOK_NO_ELIGIBLE_EXPERT: "Quick Book unavailable",
  NORMAL_BOOKING_NO_TIME_SLOTS: "Normal booking unavailable",
  NORMAL_BOOKING_LOCATION_UNAVAILABLE: "Location unavailable",
};

const eventTypeLabel = (row) => {
  if (row.eventType) return EVENT_TYPE_LABEL[row.eventType] || row.eventType;
  return "Normal search";
};

const eventTypeColor = (row) => {
  if (row.eventType === "QUICK_BOOK_NO_ELIGIBLE_EXPERT") return "warning";
  if (["NORMAL_BOOKING_NO_TIME_SLOTS", "NORMAL_BOOKING_LOCATION_UNAVAILABLE"].includes(row.eventType)) return "error";
  return "info";
};

const SEARCH_TYPE_LABEL = { "1": "Service", "2": "Partner", "3": "Storefront" };

const formatTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  }).format(date);
};

export const quickBookLocationLabel = (location = {}) => (
  [...new Set([location.address, location.name, location.city, location.state, location.pincode].filter(Boolean))].join(", ") || "—"
);

const searchLocationLabel = (row = {}) => {
  const label = quickBookLocationLabel(row.location);
  const latitude = row.latitude ?? row.searchQuery?.lat ?? row.searchQuery?.latitude;
  const longitude = row.longitude ?? row.searchQuery?.long ?? row.searchQuery?.longitude;
  return label === "—" && latitude != null && longitude != null ? `${latitude}, ${longitude}` : label;
};

const searchLabel = (row = {}) => row.service?.name
  || row.searchQuery?.serviceTitle
  || row.searchQuery?.searchText
  || row.searchQuery?.query
  || row.query
  || "—";

const searchResultCount = (row = {}) => row.resultCount
  ?? (Array.isArray(row.result) ? row.result.length : null)
  ?? row.quickBookResultCount;

const bookingFlowLabel = (row = {}) => row.bookingFlow || (!row.eventType ? "NORMAL (legacy)" : "—");
const searchType = (row = {}) => row.type ?? row.searchQuery?.type;

const DetailField = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{value === null || value === undefined || value === "" ? "—" : value}</Typography>
  </Grid>
);

const EmptyRows = () => (
  <Box sx={{ display: "grid", height: "100%", placeItems: "center", p: 2 }}>
    <Typography color="text.secondary">No search history records found.</Typography>
  </Box>
);

export default function QuickBookSearchHistory() {
  const dispatch = useDispatch();
  const loadId = useRef(0);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [selectedRow, setSelectedRow] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerLoading, setCustomerLoading] = useState(false);
  const { control, watch } = useForm({ defaultValues: { search: "", flow: "", eventType: "", from: "", to: "" } });
  const [searchInput, flow, eventType, from, to] = watch(["search", "flow", "eventType", "from", "to"]);
  const [search, setSearch] = useState("");
  const invalidDateRange = Boolean(from && to && from > to);

  useEffect(() => {
    dispatch({ type: "menuSelect", payload: "quick-book-search-history" });
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPaginationModel((current) => current.page === 0 ? current : { ...current, page: 0 });
  }, [search, flow, eventType, from, to]);

  const fetchHistory = useCallback(async () => {
    if (invalidDateRange) return;
    const requestId = ++loadId.current;
    setLoading(true);
    setError("");
    const response = await QuickBookService.searchHistory({
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      search, flow, eventType, from, to,
    });
    if (requestId !== loadId.current) return;
    if (ok(response)) {
      setRows(response.data?.items || []);
      setRowCount(response.data?.pagination?.totalItems || 0);
    } else {
      setRows([]);
      setRowCount(0);
      setError(response?.message || "Search history could not be loaded.");
    }
    setLoading(false);
  }, [search, flow, eventType, from, to, invalidDateRange, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    fetchHistory();
    return () => { loadId.current += 1; };
  }, [fetchHistory]);

  useEffect(() => {
    if (!selectedRow) { setCustomerName(""); return; }
    if (selectedRow.customer?.name) { setCustomerName(selectedRow.customer.name); return; }
    const customerId = selectedRow.customerId || selectedRow.searchBy || selectedRow.userId;
    if (!customerId) { setCustomerName(""); return; }
    setCustomerLoading(true);
    CustomerService.fetchCustomerDetailById(customerId).then((res) => {
      if (ok(res)) {
        const p = res.data?.profile;
        setCustomerName(p ? [p.firstName, p.lastName].filter(Boolean).join(" ") : "");
      } else {
        setCustomerName("");
      }
      setCustomerLoading(false);
    }).catch(() => { setCustomerName(""); setCustomerLoading(false); });
  }, [selectedRow]);

  const columns = useMemo(() => [
    {
      field: "eventType",
      headerName: "Type",
      minWidth: 155,
      sortable: false,
      renderCell: ({ row }) => (
        <Chip size="small" color={eventTypeColor(row)} variant="outlined" label={eventTypeLabel(row)} sx={{ height: 20, fontSize: ".62rem" }} />
      ),
    },
    {
      field: "customerId",
      headerName: "Customer",
      minWidth: 140,
      renderCell: ({ row }) => {
        const id = row.customerId || row.searchBy || row.userId;
        return (
          <Box>
            <Typography sx={{ fontSize: ".75rem", fontWeight: 700 }}>{row.customer?.name || (id ? `#${id}` : "—")}</Typography>
            {row.customer?.name && id && <Typography color="text.secondary" sx={{ fontSize: ".68rem" }}>#{id}</Typography>}
          </Box>
        );
      },
    },
    {
      field: "service",
      headerName: "Service / Query",
      minWidth: 220,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Typography sx={{ fontSize: ".76rem", fontWeight: 700 }}>
            {searchLabel(row)}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} useFlexGap flexWrap="wrap">
            <Chip size="small" color="primary" variant="outlined" label={bookingFlowLabel(row)} sx={{ height: 20, fontSize: ".62rem" }} />
            {row.quantity != null && <Chip size="small" label={`Qty ${row.quantity}`} sx={{ height: 20, fontSize: ".62rem" }} />}
            {searchResultCount(row) != null && <Chip size="small" label={`Results ${searchResultCount(row)}`} sx={{ height: 20, fontSize: ".62rem" }} />}
            {searchType(row) != null && <Chip size="small" label={SEARCH_TYPE_LABEL[searchType(row)] || `Type ${searchType(row)}`} sx={{ height: 20, fontSize: ".62rem" }} />}
          </Stack>
        </Box>
      ),
    },
    {
      field: "expert",
      headerName: "Expert / Mobile",
      minWidth: 170,
      sortable: false,
      renderCell: ({ row }) => row.expert?.id ? (
        <Box>
          <Typography sx={{ fontSize: ".74rem", fontWeight: 700 }}>{row.expert.name || `Expert #${row.expert.id}`}</Typography>
          <Typography color="text.secondary" sx={{ fontSize: ".68rem" }}>#{row.expert.id}</Typography>
        </Box>
      ) : row.mobileNumber ? (
        <Typography sx={{ fontSize: ".72rem" }}>{row.mobileNumber}</Typography>
      ) : <Typography color="text.secondary" sx={{ fontSize: ".72rem" }}>—</Typography>,
    },
    {
      field: "selectedSlot",
      headerName: "Selected slot",
      minWidth: 200,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => row.selectedSlot?.startAt ? (
          <Box>
            <Typography sx={{ fontSize: ".72rem", fontWeight: 600 }}>{formatTime(row.selectedSlot.startAt)}</Typography>
            <Typography color="text.secondary" sx={{ fontSize: ".68rem" }}>to {formatTime(row.selectedSlot.endAt)}</Typography>
          </Box>
        ) : <Typography sx={{ fontSize: ".72rem", fontWeight: 600 }}>{row.selectedSlot?.date || "—"}</Typography>,
    },
    {
      field: "location",
      headerName: "Location",
      minWidth: 200,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography sx={{ fontSize: ".72rem" }}>
          {searchLocationLabel(row)}
        </Typography>
      ),
    },
    {
      field: "createdAt",
      headerName: "Recorded",
      minWidth: 180,
      renderCell: ({ value }) => <Typography sx={{ fontSize: ".72rem" }}>{formatTime(value)}</Typography>,
    },
    {
      field: "actions",
      headerName: "",
      width: 52,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Tooltip title="View detail">
          <IconButton size="small" aria-label="View detail" onClick={() => setSelectedRow(row)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], []);

  const customerId = selectedRow?.customerId || selectedRow?.searchBy || selectedRow?.userId;
  const resultItems = [selectedRow?.result, selectedRow?.quickBookResults, selectedRow?.experts]
    .find((items) => Array.isArray(items) && items.length) || [];

  return (
    <>
      <PageHeaderLayout headerTitle="Search History">
        <Tooltip title="Refresh search history">
          <span>
            <IconButton aria-label="Refresh search history" onClick={fetchHistory} disabled={loading || invalidDateRange}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </PageHeaderLayout>

      <Typography color="text.secondary" sx={{ mb: 1.5 }}>Customer booking searches including unavailable slot events and normal searches.</Typography>
      <Card variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2, boxShadow: "0 4px 16px rgba(15, 23, 42, 0.05)" }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Filters</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap flexWrap="wrap" alignItems={{ sm: "center" }}>
          <Controller name="search" control={control} render={({ field }) => (
            <TextField {...field} size="small" label="Customer, mobile, query or expert" inputProps={{ maxLength: 100 }} sx={{ minWidth: { sm: 280 } }} />
          )} />
          <Controller name="flow" control={control} render={({ field }) => (
            <TextField {...field} select size="small" label="Booking flow" sx={{ minWidth: 200 }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="QUICK_BOOK">Quick Book</MenuItem>
              <MenuItem value="NORMAL">Normal</MenuItem>
            </TextField>
          )} />
          <Controller name="eventType" control={control} render={({ field }) => (
            <TextField {...field} select size="small" label="Event type" sx={{ minWidth: 220 }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="QUICK_BOOK_NO_ELIGIBLE_EXPERT">Quick Book unavailable</MenuItem>
              <MenuItem value="NORMAL_BOOKING_NO_TIME_SLOTS">Normal booking unavailable</MenuItem>
              <MenuItem value="NORMAL_BOOKING_LOCATION_UNAVAILABLE">Location unavailable</MenuItem>
              <MenuItem value="NORMAL_SEARCH">Normal search</MenuItem>
            </TextField>
          )} />
          <Controller name="from" control={control} render={({ field }) => (
            <TextField {...field} type="date" size="small" label="Recorded from" InputLabelProps={{ shrink: true }} inputProps={{ max: to || undefined }} />
          )} />
          <Controller name="to" control={control} render={({ field }) => (
            <TextField {...field} type="date" size="small" label="Recorded to" InputLabelProps={{ shrink: true }} inputProps={{ min: from || undefined }} />
          )} />
        </Stack>
      </Card>
      {invalidDateRange && <Alert severity="warning" sx={{ mb: 1 }}>Recorded from must be on or before recorded to.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Card variant="outlined" sx={{ width: "100%", overflowX: "auto", borderRadius: 2, boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)" }}>
        <Box sx={{ minWidth: 1180 }}>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            getRowHeight={() => "auto"}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50]}
            disableRowSelectionOnClick
            slots={{ noRowsOverlay: EmptyRows }}
            sx={{ border: 0, "& .MuiDataGrid-columnHeaders": { bgcolor: "grey.50" }, "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 }, "& .MuiDataGrid-cell": { minHeight: 68, py: 1 } }}
          />
        </Box>
      </Card>

      <Dialog open={!!selectedRow} onClose={() => setSelectedRow(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography component="span" sx={{ fontWeight: 700 }}>Search record detail</Typography>
            {selectedRow && (
              <Chip size="small" color={eventTypeColor(selectedRow)} variant="outlined" label={eventTypeLabel(selectedRow)} sx={{ ml: 1, height: 20, fontSize: ".62rem" }} />
            )}
          </Box>
          <IconButton aria-label="Close" onClick={() => setSelectedRow(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRow && (
            <Stack spacing={2}>
              <Grid container spacing={1}>
                <DetailField label="Record ID" value={selectedRow.id} />
                <DetailField label="Customer ID" value={customerId ? `#${customerId}` : null} />
                <DetailField label="Customer name" value={customerLoading ? "Loading…" : customerName} />
                <DetailField label="Mobile" value={selectedRow.mobileNumber} />
                <DetailField label="Booking flow" value={bookingFlowLabel(selectedRow)} />
                <DetailField label="Event type" value={selectedRow.eventType || "Normal search"} />
                <DetailField label="Service / query" value={searchLabel(selectedRow)} />
                {selectedRow.searchQuery && <DetailField label="Search request" value={JSON.stringify(selectedRow.searchQuery)} />}
                {selectedRow.quantity != null && <DetailField label="Quantity" value={selectedRow.quantity} />}
                {searchResultCount(selectedRow) != null && <DetailField label="Result count" value={searchResultCount(selectedRow)} />}
                {searchType(selectedRow) != null && <DetailField label="Search type" value={SEARCH_TYPE_LABEL[searchType(selectedRow)] || searchType(selectedRow)} />}
                {selectedRow.expert?.id && <DetailField label="Expert" value={`${selectedRow.expert.name || ""} #${selectedRow.expert.id}`} />}
                {selectedRow.bookingId && <DetailField label="Booking ID" value={`#${selectedRow.bookingId}`} />}
                {selectedRow.selectedSlot?.startAt && <DetailField label="Slot start" value={formatTime(selectedRow.selectedSlot.startAt)} />}
                {selectedRow.selectedSlot?.endAt && <DetailField label="Slot end" value={formatTime(selectedRow.selectedSlot.endAt)} />}
                {selectedRow.selectedSlot?.date && !selectedRow.selectedSlot?.startAt && <DetailField label="Slot date" value={selectedRow.selectedSlot.date} />}
                <DetailField label="Location" value={searchLocationLabel(selectedRow)} />
                {selectedRow.reason && <DetailField label="Reason" value={selectedRow.reason} />}
                <DetailField label="Recorded at" value={formatTime(selectedRow.createdAt)} />
              </Grid>
              {resultItems.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Search results ({resultItems.length})</Typography>
                  <Stack spacing={0.5}>
                    {resultItems.map((item, idx) => (
                      <Card key={idx} variant="outlined" sx={{ p: 1, fontSize: ".74rem" }}>
                        {item.firstName || item.lastName ? (
                          <Typography sx={{ fontSize: ".74rem", fontWeight: 600 }}>{[item.firstName, item.lastName].filter(Boolean).join(" ")}</Typography>
                        ) : null}
                        {item.address && <Typography color="text.secondary" sx={{ fontSize: ".7rem" }}>{item.address}</Typography>}
                        {(item.title || item.serviceTitle || item.expertName) && <Typography sx={{ fontSize: ".74rem", fontWeight: 600 }}>{item.title || item.serviceTitle || item.expertName}</Typography>}
                        {(item.id || item.serviceId || item.expertId) && !item.firstName && <Typography sx={{ fontSize: ".72rem" }}>ID: {item.id || item.serviceId || item.expertId}</Typography>}
                        {item.expertName && item.serviceTitle && <Typography color="text.secondary" sx={{ fontSize: ".7rem" }}>Expert: {item.expertName}</Typography>}
                        {item.distanceKm && <Typography color="text.secondary" sx={{ fontSize: ".7rem" }}>Distance: {item.distanceKm}</Typography>}
                      </Card>
                    ))}
                  </Stack>
                </Box>
              )}
              {resultItems.length === 0 && (
                <Alert severity="info">No data were found.</Alert>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
